import { Node, mergeAttributes, ReactNodeViewRenderer, ReactNodeViewProps } from '@tiptap/react';

import { ComponentType } from 'react';

import { RiskMatrixNodeComponent } from './risk-matrix-node-component';
import { RISK_MATRIX_CONSTANTS } from './constants';

/**
 *
 * We need to implement a custom Risk Matrix node in the TipTap editor for the Documents module (Document tab).
 *
 * This is required to support Risk Assessments and similar documents.
 *
 * Requirements:
 *
 * 5x5 matrix of severity × likelihood, scoring 1–25.
 *
 * Editable labels.
 *
 * Configurable size (rows/columns) — allow configuring number of rows and columns, not hardcoded 5x5.
 *
 * UI/UX and functionality should follow the current Risk Assessments implementation.
 *
 * Node must be fully embeddable in the TipTap editor.
 *
 * Acceptance Criteria:
 * ✅ Custom Risk Matrix node is implemented and available in TipTap editor in the Documents module.
 * ✅ Users can add, edit, and configure the matrix.
 * ✅ Labels are editable.
 * ✅ Size (rows/columns) is configurable.
 * ✅ Styling is consistent with Workspace.
 * ✅ Matrix data is persisted correctly in the Document.
 *
 */

export interface RiskMatrixNodeOptions {
  /**
   * Default number of rows in the matrix
   * @default 5
   */
  defaultRows?: number;
  /**
   * Default number of columns in the matrix
   * @default 5
   */
  defaultColumns?: number;
  /**
   * Default impact labels
   * @default ['Insignificant', 'Minor', 'Significant', 'Major', 'Severe']
   */
  defaultImpactLabels?: string[];
  /**
   * Default likelihood labels
   * @default ['Almost certain', 'Likely', 'Moderate', 'Unlikely', 'Rare']
   */
  defaultLikelihoodLabels?: string[];
  /**
   * Whether the matrix is editable
   * @default true
   */
  editable?: boolean;
  /**
   * Whether to show the summary in the document
   * @default true
   */
  showSummary?: boolean;
}

declare module '@tiptap/react' {
  interface Commands<ReturnType> {
    riskMatrix: {
      setRiskMatrixNode: (options?: RiskMatrixNodeOptions) => ReturnType;
    };
  }
}

export interface RiskMatrixData {
  rows: number;
  columns: number;
  impactLabels: string[];
  likelihoodLabels: string[];
  selectedImpact: number;
  selectedLikelihood: number;
  selectedRisk: number;
}

export const RiskAssessmentsNode = Node.create<RiskMatrixNodeOptions>({
  name: 'riskMatrix',

  group: 'block',

  draggable: false,

  selectable: true,

  atom: true,

  addOptions() {
    return {
      defaultRows: RISK_MATRIX_CONSTANTS.DEFAULT_ROWS,
      defaultColumns: RISK_MATRIX_CONSTANTS.DEFAULT_COLUMNS,
      defaultImpactLabels: RISK_MATRIX_CONSTANTS.DEFAULT_IMPACT_LABELS,
      defaultLikelihoodLabels: RISK_MATRIX_CONSTANTS.DEFAULT_LIKELIHOOD_LABELS,
      editable: true,
      showSummary: true,
    };
  },

  addAttributes() {
    return {
      rows: {
        default: this.options.defaultRows,
      },
      columns: {
        default: this.options.defaultColumns,
      },
      impactLabels: {
        default: this.options.defaultImpactLabels,
      },
      likelihoodLabels: {
        default: this.options.defaultLikelihoodLabels,
      },
      selectedImpact: {
        default: 3,
      },
      selectedLikelihood: {
        default: 3,
      },
      selectedRisk: {
        default: 9,
      },
      editable: {
        default: this.options.editable,
      },
      showSummary: {
        default: this.options.showSummary,
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="risk-matrix"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes({ 'data-type': 'risk-matrix' }, HTMLAttributes)];
  },
  addNodeView() {
    return ReactNodeViewRenderer(RiskMatrixNodeComponent as unknown as ComponentType<ReactNodeViewProps>);
  },

  addCommands() {
    return {
      setRiskMatrixNode:
        (options = {}) =>
        ({ commands }) =>
          commands.insertContent({
            type: this.name,
            attrs: {
              rows: options.defaultRows || this.options.defaultRows,
              columns: options.defaultColumns || this.options.defaultColumns,
              impactLabels: options.defaultImpactLabels || this.options.defaultImpactLabels,
              likelihoodLabels: options.defaultLikelihoodLabels || this.options.defaultLikelihoodLabels,
              editable: options.editable !== undefined ? options.editable : this.options.editable,
              showSummary: options.showSummary !== undefined ? options.showSummary : this.options.showSummary,
            },
          }),
    };
  },

  addKeyboardShortcuts() {
    return {
      Enter: ({ editor }) => {
        const { selection } = editor.state;
        const { nodeAfter } = selection.$from;

        if (nodeAfter && nodeAfter.type.name === 'riskMatrix' && editor.isActive('riskMatrix')) {
          // Allow Enter to create a new paragraph after the matrix
          return false;
        }
        return false;
      },
    };
  },
});
